// App shell: domain details, integrations, profile/user details, profile settings, navigation, profiles.
// Branding (colours, logos, title) is copied from what the parent page already loaded, so the preview
// looks like your domain. Every switch that controls access is fixed to fully enabled, so pages never
// redirect or show a permission screen inside the preview.
import { PREVIEW_PROFILE_ID, PREVIEW_PROFILE_NAME } from '../constants';

// Set by the parent page (RealPagePreview) from data it had already loaded. No request involved.
const parentShell = () => {
	try {
		return (window.parent && window.parent.__LC_PREVIEW_SHELL__) || {};
	} catch (e) {
		return {};
	}
};

// Settings sidebar entries (Settings/childrens/SideBarRoutesList.js) by left nav page key.
// Only these switches follow the previewed role; keys shared with module pages stay on.
const SETTINGS_PAGE_KEYS = {
	'settings-profile': 'PROFILE_DETAILS',
	'settings-integrations': 'INTEGRATIONS',
	'settings-inviteUser': 'MANAGE_USERS',
	'settings-manageRoles': 'MANAGE_ROLES',
	'settings-manageTeams': 'MANAGE_TEAMS',
	'settings-customNavigation': 'CUSTOME_SIDEBAR',
	'settings-savedTagsAndFilters': 'MANAGE_FILTERS',
	'settings-claimLocation': 'CLAIM_LOCATION',
};

// True when the previewed role can open this left nav page (all pages if the parent sent no list).
export const settingsPageVisible = (pageId) => {
	const pages = parentShell().visiblePages;
	return !Array.isArray(pages) || pages.indexOf(pageId) !== -1;
};

// The settings sidebar shows an entry only when its switch is on.
const settingsSidebarControl = () => {
	const control = all(SETTINGS_KEYS);
	Object.keys(SETTINGS_PAGE_KEYS).forEach((pageId) => {
		const key = SETTINGS_PAGE_KEYS[pageId];
		// Billing and Manage Users share MANAGE_USERS.
		const on = key === 'MANAGE_USERS' ? settingsPageVisible('settings-inviteUser') || settingsPageVisible('settings-payment') : settingsPageVisible(pageId);
		control[key] = on;
	});
	return control;
};

const all = (keys, value = true) => keys.reduce((o, k) => ({ ...o, [k]: value }), {});

const TAB_KEYS = ['DASHBOARD', 'INSIGHTS', 'LPO', 'REVIEWS', 'MANAGE_LOCATIONS', 'MANAGE_LOCATIONS_BULK', 'COMPETITORS', 'POST_SCHEDULE', 'POST_INSIGHTS', 'SETTINGS', 'QA', 'REVIEW_SHOWCASE', 'AUTO_RESPONSE', 'IMAGE_MANAGEMENT', 'AUTOMATION_ENGINE', 'GOOGLE_AUDIT', 'REVIEW_SETTINGS', 'REPORT_MANAGEMENT', 'DATA_DOWNLOADS', 'PROFILE_DETAILS'];
const SETTINGS_KEYS = ['AUTHORIZE_ACCOUNT', 'GOOGLE_AUDIT', 'INTEGRATIONS', 'MANAGE_ROLES', 'MANAGE_USERS', 'MANAGE_TEAMS', 'REVIEW_SETTINGS', 'CAMPAIGN_BUILDER', 'REPORT_MANAGEMENT', 'DATA_DOWNLOADS', 'PROFILE_DETAILS', 'CUSTOME_SIDEBAR', 'CLAIM_LOCATION', 'REVIEW_CLARITY', 'MANAGE_FILTERS', 'FILTER_COMPOSER', 'AUTOMATE', 'GOOGLE_ADS'];
const ACCESS_KEYS = ['DASHBOARD', 'INSIGHTS', 'LPO', 'REVIEWS', 'MANAGE_LOCATIONS', 'MANAGE_LOCATIONS_BULK', 'COMPETITORS', 'POST_SCHEDULE', 'POST_INSIGHTS', 'SETTINGS', 'QA', 'REVIEW_SHOWCASE', 'AUTO_RESPONSE', 'IMAGE_MANAGEMENT'];
const LOCATION_FIELDS = ['close_permanently', 'close_temporarily', 'storecode', 'business_name', 'primary_category', 'regular_hours', 'holidays', 'additional_categories', 'website_link', 'phone_number', 'address', 'description', 'attribute_list', 'labels', 'opening_date', 'ad_words_location_extensions'];
const INTEGRATION_KEYS = ['airbnb', 'angieslist', 'apartments', 'appleappstore', 'applebusinessconnect', 'applemaps', 'appstore', 'avvo', 'betterbusinessbureau', 'bing', 'booking', 'capterra', 'cardealer', 'cargurus', 'cars', 'cellarpass', 'dealerrater', 'doordash', 'edmunds', 'expedia', 'facebook', 'foursquare', 'glassdoor', 'google', 'googleplaystore', 'grubhub', 'gtwo', 'healthgrades', 'highya', 'homeadvisor', 'homestars', 'hostelworld', 'hotels', 'houzz', 'indeed', 'lawyers', 'opentable', 'playstore', 'productreview', 'ratemds', 'rent', 'reserveout', 'reviewshakebooking', 'reviewshakefacebook', 'reviewshakegmb', 'reviewshaketrustpilot', 'talabat', 'tripadvisor', 'trustedshops', 'trustpilot', 'trustradius', 'ubereats', 'vitals', 'yelp', 'zillow', 'zocdoc', 'zomato'];

// Only look-and-feel fields are taken from the real domain config.
const BRANDING_KEYS = ['pageTitle', 'pageDescription', 'supportEmail', 'starIconColor', 'domainColors', 'whiteLabelImages', 'happinessRating', 'domainCustomization', 'administrationNavigation', 'defaultLanguage', 'domainLanguages'];
const pick = (obj, keys) => keys.reduce((o, k) => (obj && obj[k] !== undefined ? { ...o, [k]: obj[k] } : o), {});

const PROFILE = { profileId: PREVIEW_PROFILE_ID, profileName: PREVIEW_PROFILE_NAME, masterLocationFilter: true, subscriptionId: 'sub_preview_0001' };

const navItem = (name, icon, coreId, position) => ({
	tabId: `nav-${coreId.toLowerCase()}`, name, tooltip: name, type: 'RIGHT_NAV', link: '', visibility: true,
	icon, iconType: 'SVG', coreId, tabType: 'CORE', position, background: 'transparent', padding: 0, domainName: '',
});

export default {
	// The app renders nothing until this answers. Chat and HubSpot injection stay off.
	'/getDomainDetails': () => ({
		title: 'LocalClarity',
		description: '',
		href: '/favicon.ico',
		language: 'english',
		domainName: 'LocalClarity',
		sentimentVisibility: true,
		...(parentShell().domainDetails || {}),
		domainList: false,
		htmlScript: null,
		liveChat: false,
		chatEnabledDomains: false,
	}),

	'/integrations/read': () => ({
		pageTitle: 'LocalClarity',
		starIconColor: '#FFB400',
		whiteLabelImages: { rightNavBackground: '/images/bgbaneer.jpg' },
		happinessRating: { fiveStar: 1, fourStar: 1, threeStar: 0, twoStar: -1, oneStar: -1 },
		domainLanguages: [],
		defaultLanguage: 'english',
		...pick(parentShell().integrations, BRANDING_KEYS),
		hasAiResponse: true,
		hasSentimentAnalysis: true,
		hasLanguageDetection: true,
		showBulkLocationEdit: true,
		reviewsInboxControl: { isResponseEnabled: true },
		manageLocationControl: { scope: 'EDIT' },
		tabControl: all(TAB_KEYS),
		settingsSubTabControl: settingsSidebarControl(),
		integrations: all(INTEGRATION_KEYS),
		analyticsScript: null,
		mfaDomainList: [],
	}),

	// Must not contain a top-level `data` key (a data.status of 404 signs the user out).
	'/getProfileUserDetails': () => ({
		userId: 'dana@brightsidecoffee.com',
		role: 'OWNER',
		accessFeatures: all(ACCESS_KEYS, 'PUBLISH'),
		accessFeaturesVisibility: all(ACCESS_KEYS.concat(['AUTOMATION_ENGINE'])),
		settingSubTabsVisibility: all(SETTINGS_KEYS),
		reviewsInboxControl: { isResponseEnabled: true },
		manageLocationControl: all(LOCATION_FIELDS),
		tier: {
			tabControl: all(TAB_KEYS),
			settingsSubTabControl: all(SETTINGS_KEYS),
			reviewsInboxControl: { full_control: true, isResponseEnabled: true, hasSentimentAnalysis: true, hasAiResponse: true, hasLanguageDetection: true },
			automationControl: { full_control: true, reviews: true, googleEditNotification: true, googleqa: true, publicmedia: true },
			manageLocationControl: { full_control: true, ...all(LOCATION_FIELDS) },
		},
		profile: { ...PROFILE, hasLogo: false },
	}),

	'/getProfileSettings': () => ({
		dateFormat: { label: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
		enableAuditDataAccess: true,
		auditTableControls: all(['enableAuditGoogle', 'enableAuditGoogleUpdated', 'enableAuditApple', 'enableAuditAppleUpdated', 'enableAuditBing', 'enableAuditYelp', 'enableAuditFacebook', 'enableAuditFirstParty', 'enableAuditFetch', 'enableAuditDataAccess']),
		isScraperEnabled: true,
		scraperSettings: { recurringScraperFetch: false, scraperCronString: '', scraperTimeZone: 'America/Los_Angeles' },
		hasLanguageDetection: true,
		hasSentimentAnalysis: true,
		hasSentimentDetectionLanguage: false,
		sentimentDetectionLanguage: null,
		reviewFilterStartDate: '',
		reviewInboxSettings: { HAS_AI_RESPONSE: true, STORECODE: true, FULL_ADDRESS: true, HAS_LOCATION_TIME_ZONE: true, REVIEW_SUMMARY_WIDGET: true },
		hideRemovedFolder: false,
		replyConfirmation: true,
		reviewCloseConfirmation: true,
		reviewRemoveConfirmation: true,
		reviewTimeStamp: { timeFormat: 'UTC' },
		viewFacebookComments: true,
		autoReplyActive: true,
		sendForApproval: false,
		assignedForAllEditors: true,
		assignedEditors: [],
		delayOption: 'TENMINUTES',
		delay: { hour: 0, minute: 10 },
		timeZone: '-08:00',
		profileVariables: [],
		customProfileVariables: [],
		showBulkLocationEdit: true,
		happinessRating: { fiveStar: 1, fourStar: 1, threeStar: 0, twoStar: -1, oneStar: -1 },
		suppressLocationStates: {},
		updatedReviews: false,
		removedReviews: false,
		reviewCleanupStatus: 'COMPLETED',
		reviewCleanUpCreateTime: null,
		isBillingEnabled: true,
	}),

	// Icons must exist in utils/RightnavSvg.js (with a _fill twin).
	'/getNavigation': () => ({
		navigations: {
			top: [navItem('Filters', 'filter', 'FILTERS', 'TOP'), navItem('Notifications', 'notification', 'NOTIFICATIONS', 'TOP')],
			middle: [],
			bottom: [navItem('Language', 'language', 'LANGUAGE', 'BOTTOM'), navItem('Full Screen', 'fullScreen', 'FULL_SCREEN', 'BOTTOM')],
			left: [],
		},
	}),
	'/getEditNotifications': () => ({ unreadCount: 0, notifications: [] }),

	'/getUser': () => ({ userId: 'dana@brightsidecoffee.com', email: 'dana@brightsidecoffee.com', name: 'Dana Whitfield', userImage: null, landingPage: 'DASHBOARD' }),

	// profileName must match localStorage.profileName (set by preview mode), or the avatar can't find its profile.
	'/getProfiles': () => [{ role: 'OWNER', profile: PROFILE }],
	'/getProfilesV2': () => ({ profiles: [{ role: 'OWNER', profile: PROFILE }], total: 1 }),
};
